import * as Sentry from '@sentry/react';
import { trackError } from './analytics';

const MAX_SAFE_TEXT_LENGTH = 300;

const safeToken = (value, fallback = 'unknown') => {
  const token = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return token || fallback;
};

export const classifyError = (error) => {
  const status = Number(error?.response?.status || error?.status || 0);
  const code = String(error?.code || '').toLowerCase();
  const name = String(error?.name || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  if (name === 'aborterror' || code === 'econnaborted' || message.includes('timeout')) return 'timeout';
  if (status === 401 || status === 403) return 'auth';
  if (status === 429 || message.includes('quota') || message.includes('rate limit')) return 'rate_limited';
  if (status >= 500) return 'server';
  if (status >= 400) return 'request';
  if (message.includes('network') || message.includes('failed to fetch')) return 'network';
  if (name === 'aborterror' || message.includes('cancel')) return 'cancelled';
  return 'unknown';
};

export const sanitizeUrl = (value) => {
  if (!value) return value;
  try {
    const parsed = new URL(String(value), window.location.origin);
    const isPrivateMedia = /supabase/i.test(parsed.hostname) && /\/storage\//i.test(parsed.pathname);
    return isPrivateMedia ? `${parsed.origin}/storage/[redacted]` : `${parsed.origin}${parsed.pathname}`;
  } catch {
    return String(value).split(/[?#]/)[0].slice(0, MAX_SAFE_TEXT_LENGTH);
  }
};

const sanitizeText = (value) => String(value || '')
  .replace(/data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+/gi, '[image removed]')
  .replace(/\beyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\b/g, '[token removed]')
  .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[email removed]')
  .replace(/https?:\/\/[^\s]+/gi, (url) => sanitizeUrl(url))
  .slice(0, MAX_SAFE_TEXT_LENGTH);

const sanitizeScalarContext = (context = {}) => Object.fromEntries(
  Object.entries(context)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .slice(0, 12)
    .map(([key, value]) => [safeToken(key), typeof value === 'string' ? safeToken(value) : value])
);

export const scrubSentryBreadcrumb = (breadcrumb) => {
  if (!breadcrumb) return breadcrumb;
  if (breadcrumb.category === 'ui.input' || breadcrumb.category === 'console') return null;

  const next = { ...breadcrumb };
  if (next.message) next.message = sanitizeText(next.message);
  if (next.data) {
    const data = { ...next.data };
    if (data.url) data.url = sanitizeUrl(data.url);
    if (data.from) data.from = sanitizeUrl(data.from);
    if (data.to) data.to = sanitizeUrl(data.to);
    delete data.body;
    delete data.request_body;
    delete data.response_body;
    next.data = data;
  }
  return next;
};

export const scrubSentryEvent = (event) => {
  if (!event) return event;
  const next = { ...event };

  if (next.message) next.message = sanitizeText(next.message);
  if (next.request) {
    next.request = {
      method: next.request.method,
      url: sanitizeUrl(next.request.url),
    };
  }
  if (next.exception?.values) {
    next.exception = {
      ...next.exception,
      values: next.exception.values.map((value) => ({
        ...value,
        value: sanitizeText(value.value),
      })),
    };
  }
  next.extra = sanitizeScalarContext(next.extra);
  return next;
};

/**
 * Report a handled failure without sending chat text, image data, signed URLs,
 * email addresses, or backend response bodies to analytics/error tools.
 */
export const reportError = (error, operation, context = {}) => {
  const safeOperation = safeToken(operation, 'unknown_operation');
  const category = classifyError(error);
  const safeContext = sanitizeScalarContext(context);

  trackError(safeOperation, category);

  Sentry.withScope((scope) => {
    scope.setTag('operation', safeOperation);
    scope.setTag('error_category', category);
    Object.entries(safeContext).forEach(([key, value]) => scope.setExtra(key, value));
    const reportedError = new Error(`${safeOperation} failed (${category})`);
    reportedError.name = error?.name || 'HandledError';
    Sentry.captureException(reportedError);
  });
};

